import { useQueryClient } from '@tanstack/react-query';
import { AppContext } from 'appContext';
import { AxiosResponse } from 'axios';
import { useSaveEvidences } from 'components/Evidences/hooks/useSaveEvidences';
import { SnackBarErrors } from 'components/SnackBar/SnackBarErrors';
import { CASES_PAGE, CASES_PAGE_CREATE } from 'config/paths';
import dayjs from 'dayjs';
import { useGlobalNotification } from 'hooks/useGlobalNotification';
import useMetricEvent from 'hooks/useMetricEvent/useMetricEvent';
import { handleUploadFile } from 'pages/Asset/tabs/Events/components/Records/utils';
import { IFileManagerProps, useFileDeleteMutation, useFileUploadMutation } from 'queries';
import {
  useCreateCaseMutation,
  useDiagnosisQuery,
  useInvalidateCasesQueries,
  useSaveActionsMutation,
  useSaveDiagnosisMutation,
  useSaveFeedbackMutation,
  useUpdateCaseMutation,
} from 'queries/cases/queries';
import {
  Action,
  actionStatus,
  ActionTypes,
  CaseForm,
  CaseListItem,
  CasePayload,
  caseStatus,
  detectionMethodOptions,
  FileEvidences,
} from 'queries/cases/types';
import { useInvalidateRecordsQueries } from 'queries/events/records';
import { UseMutationCallbackFunction } from 'queries/events/records/types';
import { usePermissionsQuery } from 'queries/userAndPermissions';
import { useCallback, useContext, useEffect, useState } from 'react';
import { FieldErrors, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getUploadFileComment, utcDatetime } from 'utils';

const createExternalReference = () => ({ code: '', type: '', status: '' });

export const createEmptyAction = (): Action => ({
  creation_date: dayjs().utc().format(),
  deleted: false,
  details: '',
  due_date: '',
  end_date: '',
  execution_feedback: '',
  maintenance_notification: '',
  external_reference: createExternalReference(),
  name: '',
  status: actionStatus.on_track,
  type_case_action: ActionTypes.maintenance,
});

export const getCaseFormDefaultValues = (): CaseForm => ({
  actions: [],
  evidences: [],
  alarms: [],
  anomaly_end: null,
  anomaly_start: null,
  asset: { id: '', name: '' },
  cancel_reason: '',
  conclusion: '',
  detection_method: detectionMethodOptions.lighthouse,
  diagnosis: '',
  dimension: '',
  discipline: '',
  event_code: null,
  feedback_comments: '',
  has_savings: false,
  is_able_to_operate: true,
  name: null,
  owner_mail: '',
  review_date: null,
  savings: {},
  severity: '',
  start_date: '',
  status: caseStatus.diagnosis,
  team_a_review: null,
  team_b_review: null,
  updated_at: null,
});

const useDiagnosisForm = (data?: Partial<CaseForm | CaseListItem>) => {
  const { t } = useTranslation();
  const { displaySnackbar } = useGlobalNotification();
  const { state } = useContext(AppContext);
  const currentUser = state.auth.user;
  const username = currentUser?.name || '';
  const { dispatchMetricEvent } = useMetricEvent();
  const queryClient = useQueryClient();
  const { data: permissionsData } = usePermissionsQuery();
  const isMutating = queryClient.isMutating() > 0;
  const isPermitted = permissionsData?.permissions?.includes('READ_WRITE');

  const { caseId: urlCaseId } = useParams();
  const location = useLocation();

  const navigate = useNavigate();

  const [caseId, setCaseId] = useState(urlCaseId || data?.id || '');

  const {
    data: caseData,
    isSuccess: caseSuccess,
    isFetching: caseFetching,
  } = useDiagnosisQuery(caseId);

  const { filesData, handleSubmitComment, fileKeys } = useSaveEvidences({
    eventId: caseId,
  });

  const submitComment = (comment?: string) => {
    if (comment) {
      handleSubmitComment?.(comment.trim());
    }
  };

  const onUploadIsSuccessSentComment: UseMutationCallbackFunction<{
    formData: unknown;
    fileName?: string;
  }> = (data, variables, context) => {
    const name = variables.fileName || 'untitled';
    const message = getUploadFileComment('attached_file', username, name);
    submitComment(message);
  };

  const onError = () => {
    formMethods.resetField('status');
    displaySnackbar({
      message: t('messages:fetch_error.message'),
      type: 'error',
    });
  };

  const onDeleteIsSuccessSentComment: UseMutationCallbackFunction<IFileManagerProps> = (
    data,
    variables,
  ) => {
    const name = variables.fileName || 'untitled';
    const message = getUploadFileComment('detached_file', username, name);
    submitComment(message);
  };

  const fileUploadMutation = useFileUploadMutation(onError, onUploadIsSuccessSentComment);
  const deleteFileMutation = useFileDeleteMutation(onDeleteIsSuccessSentComment);

  const getDefaultValues = useCallback(
    (overrideDefaultActions?: Action[]) => {
      const storedEvidences: FileEvidences[] =
        filesData?.files.map((name) => ({
          name,
          isNew: false,
          isDeleted: false,
        })) || [];
      const selectedItemOnNavigation = location?.state?.selectedItem;

      return {
        ...getCaseFormDefaultValues(),
        owner_mail: currentUser?.email,
        ...data,
        ...caseData,
        ...selectedItemOnNavigation,
        updated_at: caseData?.updated_at || data?.updated_at || null,
        evidences: storedEvidences,
        actions:
          (overrideDefaultActions ?? []).length > 0
            ? overrideDefaultActions
            : caseData?.actions ?? data?.actions,
      } as CaseForm;
    },
    [caseId, currentUser, caseData, filesData, data],
  );

  const formMethods = useForm<CaseForm>({
    mode: 'all',
    defaultValues: getDefaultValues(),
  });

  const { getFieldState } = formMethods;

  useEffect(() => {
    if (caseSuccess) {
      const [id, actions] = formMethods.getValues(['id', 'actions']);
      const isSameCase = id === caseData?.id;

      formMethods.reset(getDefaultValues(isSameCase ? actions : []), {
        keepDirty: false,
      });
    }

    if (data?.id) {
      setCaseId(data.id);
    }
  }, [caseData, caseSuccess, filesData?.files, data, caseData?.status]);

  const onRequestSuccess = (response: AxiosResponse, variables: Partial<CasePayload>) => {
    const type = response.config.method === 'put' ? 'edit' : 'create';

    dispatchMetricEvent<Partial<CaseForm | CasePayload>>({
      name: `CASE_ACTION`,
      payload: { caseId, type },
      newFormData: variables,
      oldFormData: caseData,
    });
    displaySnackbar({
      message: t(`case:messages.${type}.message`),
      type: 'success',
    });
  };

  const createMutation = useCreateCaseMutation(onRequestSuccess, onError);
  const updateMutation = useUpdateCaseMutation(onRequestSuccess, onError);
  const saveDiagnosisMutation = useSaveDiagnosisMutation(
    caseData?.id ?? '',
    onRequestSuccess,
    onError,
  );

  const saveFeedbackMutation = useSaveFeedbackMutation(
    caseData?.id ?? '',
    onRequestSuccess,
    onError,
  );
  const saveActionsMutation = useSaveActionsMutation(onRequestSuccess, onError);
  const refetchRecords = useInvalidateRecordsQueries(caseId);
  const refetchCase = useInvalidateCasesQueries();

  const refetchQueries = (id = '') => {
    refetchCase(id);
    refetchRecords();

    // for Edit Case page /cases/:id
    if (urlCaseId) {
      queryClient.removeQueries(['GET_CASES']);
    }

    queryClient.invalidateQueries(['GET_CASE_ACTIONS', id]);

    if (getFieldState('alarms').isDirty) {
      queryClient.removeQueries(['GET_CASE_ALARMS']);
      queryClient.invalidateQueries(['GET_CASE_ALARMS']);
    }
  };

  const createHandler = (payload: CasePayload) => {
    if (payload === undefined || !payload) throw new Error('No data');
    createMutation.mutate(payload, {
      onSuccess: (res) => {
        // RHF load data from API
        setCaseId(res.data.id);

        if (location.pathname === CASES_PAGE_CREATE) {
          navigate(CASES_PAGE(res.data.id));
        }
        refetchQueries(res.data.id);
      },
    });
  };

  const editHandler = (payload: CasePayload) => {
    updateMutation.mutate(payload, {
      onSuccess: () => {
        refetchQueries(payload.id);
      },
    });
  };

  const saveDiagnosisHandler = (payload: CasePayload) => {
    saveDiagnosisMutation.mutate(payload, {
      onSuccess: () => {
        refetchQueries(payload.id);
      },
    });
  };

  const saveActionsHandler = (payload: CasePayload) => {
    saveActionsMutation.mutate(payload, {
      onSuccess: () => {
        refetchQueries(payload.id);
      },
    });
  };

  const saveFeedbackHandler = (payload: CasePayload) => {
    saveFeedbackMutation.mutate(payload, {
      onSuccess: () => {
        refetchQueries(payload.id);
      },
    });
  };

  const formatData = (data: CaseForm): CasePayload => {
    const formattedData = Object.keys(data).reduce((acc, field) => {
      if (field === 'anomaly_start' || field === 'anomaly_end') {
        return { ...acc, [field]: utcDatetime(data[field]) };
      }

      if (field === 'actions') {
        const formattedActions = data.actions.map((action: Action) => ({
          ...action,
          due_date: utcDatetime(action.due_date),
          end_date: utcDatetime(action.end_date),
        }));
        return { ...acc, actions: formattedActions };
      }

      if (field === 'asset') {
        return {
          ...acc,
          asset_id: data.asset?.id ?? '',
        };
      }

      if (field === 'alarms') {
        return {
          ...acc,
          alarms: data.alarms.map((alarm) => ({
            id: alarm.id,
            is_automatic: alarm?.is_automatic ?? false,
            name: alarm.name,
          })),
        };
      }

      if (field === 'savings' || field === 'has_savings') {
        const hasSavings = Object.values(data.savings).some((value) => Boolean(value));

        return {
          ...acc,
          savings: hasSavings ? data.savings : {},
          has_savings: hasSavings,
        };
      }

      return {
        ...acc,
        [field]: data[field as keyof CaseForm],
      };
    }, {} as CasePayload);

    return formattedData;
  };

  const handleAddEvidences = useCallback(
    (evidence: FileEvidences) => {
      const { file } = evidence;
      if (file) {
        handleUploadFile(file, caseId, fileUploadMutation);
      }
    },
    [caseId, fileUploadMutation],
  );

  const handleRemoveEvidences = useCallback(
    (evidence: FileEvidences) => {
      if (fileKeys) {
        deleteFileMutation.mutate({ ...fileKeys, fileName: evidence.name });
      }
    },
    [fileKeys, deleteFileMutation],
  );

  const uploadEvidences = useCallback(
    (evidences: FileEvidences[]) => {
      for (const evidence of evidences) {
        if (evidence.isNew) {
          handleAddEvidences(evidence);
        } else if (evidence.isDeleted) {
          handleRemoveEvidences(evidence);
        }
      }
    },
    [handleAddEvidences, handleRemoveEvidences],
  );

  const onSubmit = useCallback(
    (formData: CaseForm) => {
      if (formData.cancel_reason) submitComment(formData.cancel_reason);
      if (formData.evidences) {
        uploadEvidences(formData.evidences);
      }
      const formattedData = formatData(formData);

      if (formData.id) {
        switch (caseData?.status) {
          case caseStatus.diagnosis:
            return saveDiagnosisHandler(formattedData);
          case caseStatus.actions:
            return saveActionsHandler(formattedData);
          case caseStatus.feedback:
            return saveFeedbackHandler(formattedData);
          case caseStatus.closed:
            return editHandler(formattedData);
          default:
            return editHandler(formattedData);
        }
      }
      return createHandler(formattedData);
    },
    [
      createHandler,
      editHandler,
      saveDiagnosisHandler,
      saveFeedbackHandler,
      saveActionsHandler,
      caseData?.status,
      submitComment,
      uploadEvidences,
    ],
  );

  const onSubmitError = (errors: FieldErrors<CaseForm>) => {
    const haveErrors = Object.keys(errors).map(
      (key) => `'${key}'  ${t('messages:form_error.required')} \n`,
    );

    if (haveErrors.length > 0) {
      displaySnackbar({
        message: <SnackBarErrors text={t('messages:form_error.message')} errors={haveErrors} />,
        type: 'error',
      });
    }
  };

  return {
    onSubmit: formMethods.handleSubmit(onSubmit, onSubmitError),
    formMethods,
    isFormDisabled:
      !isPermitted ||
      caseData?.status === caseStatus.closed ||
      caseData?.status === caseStatus.canceled,
    isFormLoading: caseFetching || isMutating,
  };
};

export default useDiagnosisForm;
